import { useState, useEffect, lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { TenantProvider } from './contexts/TenantContext'
import MainLayout from './layouts/MainLayout'
import RootErrorBoundary from './components/RootErrorBoundary'
import { authApi } from './api/auth'
import { landingPagesApi } from './api/landing-pages'

const LandingPagePublic = lazy(() => import('./pages/LandingPagePublic'))
const DomainLandingPage = lazy(() => import('./pages/DomainLandingPage'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const Login = lazy(() => import('./pages/Login'))
const Signup = lazy(() => import('./pages/Signup'))
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'))
const ResetPassword = lazy(() => import('./pages/ResetPassword'))
const AccountSettings = lazy(() => import('./pages/AccountSettings'))
const ContactExplorer = lazy(() => import('./pages/ContactExplorer'))
const SegmentBuilder = lazy(() => import('./pages/SegmentBuilder'))
const DealsPipeline = lazy(() => import('./pages/DealsPipeline'))
const LandingPageBuilder = lazy(() => import('./pages/LandingPageBuilder'))
const DataImport = lazy(() => import('./pages/DataImport'))
const UserManagement = lazy(() => import('./pages/UserManagement'))
const Analytics = lazy(() => import('./pages/Analytics'))
const DataSync = lazy(() => import('./pages/DataSync'))
const DataCleaning = lazy(() => import('./pages/DataCleaning'))
const DataEnrichment = lazy(() => import('./pages/DataEnrichment'))
const ApiManagement = lazy(() => import('./pages/ApiManagement'))
const Documentation = lazy(() => import('./pages/Documentation'))
const PixelManagement = lazy(() => import('./pages/PixelManagement'))
const FunnelBuilder = lazy(() => import('./pages/FunnelBuilder'))
const TrackingSites = lazy(() => import('./pages/TrackingSites'))
const DashboardBuilder = lazy(() => import('./pages/DashboardBuilder'))
const AdminSettings = lazy(() => import('./pages/AdminSettings'))
const DataMap = lazy(() => import('./pages/DataMap'))
const DataQuality = lazy(() => import('./pages/DataQuality'))
const DataLineage = lazy(() => import('./pages/DataLineage'))
const ExecutiveDashboard = lazy(() => import('./pages/ExecutiveDashboard'))
const EmailVerification = lazy(() => import('./pages/EmailVerification'))
const CallInsights = lazy(() => import('./pages/CallInsights'))
const MeetingBots = lazy(() => import('./pages/MeetingBots'))
const TenantManagement = lazy(() => import('./pages/TenantManagement'))
const KBIntegration = lazy(() => import('./pages/KBIntegration'))
const CompanyExplorer = lazy(() => import('./pages/CompanyExplorer'))
const PublicHomePage = lazy(() => import('./pages/PublicHomePage'))
const CalendarSync = lazy(() => import('./pages/CalendarSync'))
const SponsorshipDeckBuilder = lazy(() => import('./pages/SponsorshipDeckBuilder'))
const SponsorshipDeckViewer = lazy(() => import('./pages/SponsorshipDeckViewer'))
const SponsorshipDeckAnalytics = lazy(() => import('./pages/SponsorshipDeckAnalytics'))
const FormsManager = lazy(() => import('./pages/FormsManager'))
const FormBuilder = lazy(() => import('./pages/FormBuilder'))
const FormPublic = lazy(() => import('./pages/FormPublic'))
const FormSubmissions = lazy(() => import('./pages/FormSubmissions'))
const ConferenceTickets = lazy(() => import('./pages/ConferenceTickets'))
const BrandCitations = lazy(() => import('./pages/BrandCitations'))
const SemanticMetrics = lazy(() => import('./pages/SemanticMetrics'))
const RevenueAttribution = lazy(() => import('./pages/RevenueAttribution'))
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'))

function ProtectedRoute({ children }) {
  const { data: user, isLoading, error } = useQuery({
    queryKey: ['auth', 'user'],
    queryFn: authApi.getCurrentUser,
    retry: false,
  })

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    )
  }

  if (error || !user) {
    return <Navigate to="/login" replace />
  }

  return <MainLayout user={user}>{children}</MainLayout>
}

function HomeRoute() {
  const { data: user, isLoading, error } = useQuery({
    queryKey: ['auth', 'user'],
    queryFn: authApi.getCurrentUser,
    retry: false,
  })

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    )
  }

  if (error || !user) {
    return <PublicHomePage />
  }

  return <MainLayout user={user}><Dashboard /></MainLayout>
}

function LoadingFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
    </div>
  )
}

const MAIN_DOMAINS = ['qoralytics.com', 'www.qoralytics.com', 'localhost', '127.0.0.1']
const MAIN_APP_URL = import.meta.env.VITE_MAIN_APP_URL || ''

function DomainRouter({ children }) {
  const [domainPage, setDomainPage] = useState(null)
  const [isChecking, setIsChecking] = useState(true)
  
  useEffect(() => {
    const checkDomain = async () => {
      const hostname = window.location.hostname
      
      const isMainDomain = MAIN_DOMAINS.some(d => hostname === d) || 
        hostname.endsWith('.replit.dev') || 
        hostname.endsWith('.replit.app') || 
        hostname.endsWith('.repl.co')
      
      if (isMainDomain) {
        setIsChecking(false)
        return
      }
      
      // 5s hard timeout so a slow lookup doesn't block the entire app shell.
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 5000)
      try {
        // Extract slug from pathname: supports /lp/my-slug or /my-slug
        const pathname = window.location.pathname
        const slugMatch = pathname.match(/^(?:\/lp)?\/([^/]+)/)
        const slug = slugMatch ? slugMatch[1] : null

        const apiBase = MAIN_APP_URL || `${window.location.protocol}//${window.location.host}`
        let url = `${apiBase}/api/landing-pages/by-domain/${encodeURIComponent(hostname)}`
        if (slug) url += `?slug=${encodeURIComponent(slug)}`

        // Public endpoint — no auth cookies, no tenant header.
        const response = await fetch(url, {
          credentials: 'omit',
          signal: controller.signal,
        })
        if (response.ok) {
          const page = await response.json()
          if (page && typeof page === 'object') setDomainPage(page)
        }
      } catch (error) {
        if (error?.name !== 'AbortError') {
          console.error('[DomainRouter] Domain lookup error:', error)
        }
      } finally {
        clearTimeout(timeoutId)
      }
      setIsChecking(false)
    }
    
    checkDomain()
  }, [])
  
  const hostname = typeof window !== 'undefined' ? window.location.hostname : ''
  const isMainDomain = MAIN_DOMAINS.some(d => hostname === d) || 
    hostname.endsWith('.replit.dev') || 
    hostname.endsWith('.replit.app') || 
    hostname.endsWith('.repl.co')
  
  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    )
  }
  
  if (domainPage) {
    return <DomainLandingPage pageData={domainPage} />
  }
  
  if (!isMainDomain && !domainPage) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center p-8">
          <h1 className="text-2xl font-bold mb-4">Page Not Found</h1>
          <p className="text-gray-600">No landing page found for this domain.</p>
        </div>
      </div>
    )
  }
  
  return children
}

export default function App() {
  return (
    <BrowserRouter>
      <RootErrorBoundary>
      <DomainRouter>
      <TenantProvider>
      <Suspense fallback={<LoadingFallback />}>
      <Routes>
        {/* Home page - shows PublicHomePage for guests, Dashboard for logged-in users */}
        <Route path="/" element={<HomeRoute />} />
        
        {/* Legacy /home route redirects to / */}
        <Route path="/home" element={<Navigate to="/" replace />} />
        
        {/* Public landing pages - no auth required */}
        <Route path="/lp/:slug" element={<LandingPagePublic />} />
        
        {/* Public forms - no auth required */}
        <Route path="/forms/public/:slug" element={<FormPublic />} />
        
        {/* Public sponsorship deck viewer */}
        <Route path="/deck/:id" element={<SponsorshipDeckViewer />} />
        
        {/* Login + self-service auth pages */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/account" element={<ProtectedRoute><AccountSettings /></ProtectedRoute>} />
        
        {/* Protected routes */}
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/contacts" element={<ProtectedRoute><ContactExplorer /></ProtectedRoute>} />
        <Route path="/companies" element={<ProtectedRoute><CompanyExplorer /></ProtectedRoute>} />
        <Route path="/segments" element={<ProtectedRoute><SegmentBuilder /></ProtectedRoute>} />
        <Route path="/deals" element={<ProtectedRoute><DealsPipeline /></ProtectedRoute>} />
        <Route path="/conference-tickets" element={<ProtectedRoute><ConferenceTickets /></ProtectedRoute>} />
        <Route path="/landing-pages" element={<ProtectedRoute><LandingPageBuilder /></ProtectedRoute>} />
        <Route path="/sponsorship-decks" element={<ProtectedRoute><SponsorshipDeckBuilder /></ProtectedRoute>} />
        <Route path="/sponsorship-analytics" element={<ProtectedRoute><SponsorshipDeckAnalytics /></ProtectedRoute>} />
        <Route path="/import" element={<ProtectedRoute><DataImport /></ProtectedRoute>} />
        <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
        <Route path="/users" element={<ProtectedRoute><UserManagement /></ProtectedRoute>} />
        <Route path="/data-sync" element={<ProtectedRoute><DataSync /></ProtectedRoute>} />
        <Route path="/data-cleaning" element={<ProtectedRoute><DataCleaning /></ProtectedRoute>} />
        <Route path="/data-enrichment" element={<ProtectedRoute><DataEnrichment /></ProtectedRoute>} />
        <Route path="/api-management" element={<ProtectedRoute><ApiManagement /></ProtectedRoute>} />
        <Route path="/documentation" element={<ProtectedRoute><Documentation /></ProtectedRoute>} />
        <Route path="/pixel-management" element={<ProtectedRoute><PixelManagement /></ProtectedRoute>} />
        <Route path="/funnels" element={<ProtectedRoute><FunnelBuilder /></ProtectedRoute>} />
        <Route path="/tracking-sites" element={<ProtectedRoute><TrackingSites /></ProtectedRoute>} />
        <Route path="/revenue" element={<ProtectedRoute><RevenueAttribution /></ProtectedRoute>} />
        <Route path="/dashboard-builder" element={<ProtectedRoute><DashboardBuilder /></ProtectedRoute>} />
        <Route path="/data-map" element={<ProtectedRoute><DataMap /></ProtectedRoute>} />
        <Route path="/data-quality" element={<ProtectedRoute><DataQuality /></ProtectedRoute>} />
        <Route path="/data-lineage" element={<ProtectedRoute><DataLineage /></ProtectedRoute>} />
        <Route path="/executive" element={<ProtectedRoute><ExecutiveDashboard /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminSettings /></ProtectedRoute>} />
        <Route path="/email-verification" element={<ProtectedRoute><EmailVerification /></ProtectedRoute>} />
        <Route path="/call-insights" element={<ProtectedRoute><CallInsights /></ProtectedRoute>} />
        <Route path="/meeting-bots" element={<ProtectedRoute><MeetingBots /></ProtectedRoute>} />
        <Route path="/calendar-sync" element={<ProtectedRoute><CalendarSync /></ProtectedRoute>} />
        <Route path="/tenants" element={<ProtectedRoute><TenantManagement /></ProtectedRoute>} />
        <Route path="/kb-integration" element={<ProtectedRoute><KBIntegration /></ProtectedRoute>} />
        <Route path="/forms" element={<ProtectedRoute><FormsManager /></ProtectedRoute>} />
        <Route path="/forms/builder/:id" element={<ProtectedRoute><FormBuilder /></ProtectedRoute>} />
        <Route path="/forms/:formId/submissions" element={<ProtectedRoute><FormSubmissions /></ProtectedRoute>} />
        <Route path="/brand-citations" element={<ProtectedRoute><BrandCitations /></ProtectedRoute>} />
        <Route path="/metrics" element={<ProtectedRoute><SemanticMetrics /></ProtectedRoute>} />
        
        {/* Fallback — friendly 404 instead of a silent redirect. */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </Suspense>
      </TenantProvider>
      </DomainRouter>
      </RootErrorBoundary>
    </BrowserRouter>
  )
}
